import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from '@/hooks/use-toast';
import {
  DollarSign,
  TrendingUp,
  Calendar,
  CreditCard,
  RefreshCw,
  Clock,
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { ClinicTransaction, ClinicSettlement } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';
import Transactions from './payment-report/Transactions';
import Settlements from './payment-report/Settlements';
import { clinicPaymentService } from '@/services/clinicPaymentService';
import { format, subDays, differenceInDays, isAfter, isBefore } from 'date-fns';
import { formatToLocalDate } from '@/lib/utils';
import MetricsCard from '../MetricsCard';
import { useClinic } from '@/contexts/ClinicContext';

export interface ProductTransaction extends ClinicTransaction {
  productId: string;
  productName: string;
  productSku: string;
  productCategory: string;
  pharmacyPrice: number;
  margin: number;
  marginPercentage: number;
  splits: any[];
}


const ClinicPaymentReports = () => {
  const { user } = useAuth();
  const { clinic } = useClinic();
  const [settlements, setSettlements] = useState<ClinicSettlement[]>([]);
  const [transactions, setTransactions] = useState<ProductTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({
    start_date: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
  });
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(
    subDays(new Date(), 7)
  );
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(
    new Date()
  );
  
  // Returns the number of days between start_date and end_date (inclusive)
  const getDateRangeDayDiff = ({
    start_date,
    end_date,
  }: {
    start_date: string;
    end_date: string;
  }) => {
    const diff = differenceInDays(new Date(end_date), new Date(start_date));

    if (diff === 7) {
      // 7 days inclusive
      return 'last7';
    } else if (diff === 30) {
      // 30 days inclusive
      return 'last30';
    } else if (diff === 90) {
      // 90 days inclusive
      return 'last90';
    }
    return 'custom';
  };
  
  const [dateRangeType, setDateRangeType] = useState<'last7' | 'last30' | 'last90' | 'custom'>(() => {
    return getDateRangeDayDiff({
      start_date: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
      end_date: format(new Date(), 'yyyy-MM-dd'),
    });
  });
  const [customRangePopoverOpen, setCustomRangePopoverOpen] = useState(false);
  

  // Metrics
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    pendingSettlements: 0,
    completedTransactions: 0,
    averageTransaction: 0,
    monthlyGrowth: 0,
  });

  // Chart data
  const [revenueData, setRevenueData] = useState<
    Array<{ date: string; amount: number; formattedDate: string }>
  >([]);

  const fetchPaymentOverview = async () => {
    if (!clinic?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await clinicPaymentService.getClinicPaymentOverview(clinic.id, {
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      });

      if (response.success) {
        setMetrics(response.data.metrics);

        // Format the revenue data for the chart
        const formattedRevenueData = (response.data.revenueData || []).map((item: any) => ({
          ...item,
          formattedDate: format(new Date(item.date), 'MMM dd'),
          amount: parseFloat(item.amount) || 0,
        }));

        setRevenueData(formattedRevenueData);
      } else {
        throw new Error(response.error || 'Failed to fetch payment overview');
      }
    } catch (error) {
      console.error('Error fetching payment overview:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch payment overview data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRecentTransactions = async () => {
    if (!clinic?.id) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await clinicPaymentService.getClinicTransactions(clinic.id, {
        page: 1,
        limit: 5,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      });

      if (response.success) {
        setTransactions(response.data.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching recent transactions:', error);
    }
  };

  useEffect(() => {
    fetchPaymentOverview();
    fetchRecentTransactions();
  }, [clinic?.id, dateRange]);

  const handleDateRangeChange = (value: string) => {
    const newType = value as 'last7' | 'last30' | 'last90' | 'custom';
    setDateRangeType(newType);
    
    switch (value) {
      case 'last7':
        const last7Start = subDays(new Date(), 7);
        setCustomStartDate(last7Start);
        setCustomEndDate(new Date());
        setDateRange({
          start_date: format(last7Start, 'yyyy-MM-dd'),
          end_date: format(new Date(), 'yyyy-MM-dd'),
        });
        break;
      case 'last30':
        const last30Start = subDays(new Date(), 30);
        setCustomStartDate(last30Start);
        setCustomEndDate(new Date());
        setDateRange({
          start_date: format(last30Start, 'yyyy-MM-dd'),
          end_date: format(new Date(), 'yyyy-MM-dd'),
        });
        break;
      case 'last90':
        const last90Start = subDays(new Date(), 90);
        setCustomStartDate(last90Start);
        setCustomEndDate(new Date());
        setDateRange({
          start_date: format(last90Start, 'yyyy-MM-dd'),
          end_date: format(new Date(), 'yyyy-MM-dd'),
        });
        break;
      case 'custom':
        // When switching to custom, use current dates or default to last 7 days
        const startDate = customStartDate || subDays(new Date(), 7);
        const endDate = customEndDate || new Date();
        
        // Ensure dates are set
        if (!customStartDate) setCustomStartDate(startDate);
        if (!customEndDate) setCustomEndDate(endDate);
        
        // Immediately update dateRange
        const customStart = format(startDate, 'yyyy-MM-dd');
        const customEnd = format(endDate, 'yyyy-MM-dd');
        setDateRange({
          start_date: customStart,
          end_date: customEnd,
        });
        // Open the popover when custom is selected
        setCustomRangePopoverOpen(true);
        break;
    }
  };

  // Handle custom date changes - update dateRange when custom dates change
  useEffect(() => {
    if (dateRangeType === 'custom' && customStartDate && customEndDate) {
      // Ensure start date is before end date
      if (isAfter(customStartDate, customEndDate)) {
        return;
      }

      const newStartDate = format(customStartDate, 'yyyy-MM-dd');
      const newEndDate = format(customEndDate, 'yyyy-MM-dd');
      
      setDateRange(prev => {
        // Only update if dates actually changed
        if (prev.start_date !== newStartDate || prev.end_date !== newEndDate) {
          return {
            start_date: newStartDate,
            end_date: newEndDate,
          };
        }
        return prev;
      });
    }
  }, [customStartDate, customEndDate, dateRangeType]);

  const handleRefresh = () => {
    fetchPaymentOverview();
    fetchRecentTransactions();
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-slate-200 rounded-lg shadow-lg">
          <p className="font-medium text-slate-900">{label}</p>
          <p className="text-sm text-brand-teal">
            Revenue: <span className="font-semibold">${payload[0].value.toFixed(2)}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Metrics configuration matching Dashboard design
  const paymentMetrics = [
    {
      title: 'Product Sales Revenue',
      value: `$${metrics.totalRevenue.toFixed(2)}`,
      icon: DollarSign,
      color: '#067A76',
    },
    {
      title: 'Total Margin',
      value: `$${metrics.pendingSettlements.toFixed(2)}`,
      icon: TrendingUp,
      color: '#3b82f6',
    },
    {
      title: 'Completed Sales',
      value: metrics.completedTransactions.toString(),
      icon: CreditCard,
      color: '#BD6F51',
    },
    {
      title: 'Avg Sale Value',
      value: `$${metrics.averageTransaction.toFixed(2)}`,
      icon: Calendar,
      color: '#f97316',
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h3 className="font-montserrat text-slate-900 font-semibold text-base sm:text-lg">Product Sales Reports</h3>
          <p className="text-slate-600 text-xs sm:text-sm mt-0.5">Track your product sales revenue and margins</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
        {paymentMetrics.map((metric, index) => (
          <MetricsCard key={index} {...metric} />
        ))}
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="settlements">Settlements</TabsTrigger>
          </TabsList>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            {dateRangeType === 'custom' ? (
              <Popover open={customRangePopoverOpen} onOpenChange={setCustomRangePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto h-9 text-xs sm:text-sm justify-between min-w-[180px]"
                  >
                    <span className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-slate-500" />
                      {customStartDate && customEndDate
                        ? `${format(customStartDate, 'MMM dd')} - ${format(customEndDate, 'MMM dd')}`
                        : 'Select date range'}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4" align="end">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-slate-900">Custom Date Range</h4>
                      <p className="text-xs text-slate-500">Select start and end dates</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-700">Start Date</label>
                        <DatePicker
                          value={customStartDate}
                          onValueChange={(date) => {
                            if (date) {
                              setCustomStartDate(date);
                              // If start date is after end date, adjust end date
                              if (customEndDate && isAfter(date, customEndDate)) {
                                setCustomEndDate(date);
                              }
                            }
                          }}
                          maxDate={customEndDate || new Date()}
                          minDate={undefined}
                          placeholder="Start date"
                          className="w-full"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-700">End Date</label>
                        <DatePicker
                          value={customEndDate}
                          onValueChange={(date) => {
                            if (date) {
                              setCustomEndDate(date);
                              // If end date is before start date, adjust start date
                              if (customStartDate && isBefore(date, customStartDate)) {
                                setCustomStartDate(date);
                              }
                            }
                          }}
                          maxDate={new Date()}
                          minDate={customStartDate}
                          placeholder="End date"
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Reset to last 7 days
                          const last7Start = subDays(new Date(), 7);
                          setCustomStartDate(last7Start);
                          setCustomEndDate(new Date());
                          setDateRangeType('last7');
                          setDateRange({
                            start_date: format(last7Start, 'yyyy-MM-dd'),
                            end_date: format(new Date(), 'yyyy-MM-dd'),
                          });
                          setCustomRangePopoverOpen(false);
                        }}
                        className="text-xs h-8"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          if (customStartDate && customEndDate) {
                            setCustomRangePopoverOpen(false);
                          }
                        }}
                        className="text-xs h-8 bg-brand-teal hover:bg-brand-teal/90"
                        disabled={!customStartDate || !customEndDate}
                      >
                        Done
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <Select value={dateRangeType} onValueChange={handleDateRangeChange}>
                <SelectTrigger className="w-full sm:w-32 md:w-40 h-9 text-xs sm:text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last7">Last 7 days</SelectItem>
                  <SelectItem value="last30">Last 30 days</SelectItem>
                  <SelectItem value="last90">Last 90 days</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <TabsContent value="overview" className="space-y-4 sm:space-y-6">
          {/* Revenue Chart - Matching Dashboard Style */}
          <Card>
            <CardHeader className="pb-2 px-3 sm:px-6">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-brand-teal/10 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-brand-teal" strokeWidth={2} />
                </div>
                <div>
                  <CardTitle className="text-slate-900 font-montserrat font-semibold text-sm sm:text-base">
                    Revenue Trend
                  </CardTitle>
                  <p className="text-slate-500 text-xs sm:text-sm mt-0.5">Your daily revenue over the selected period</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-2 sm:px-6">
              {isLoading ? (
                <div className="h-[220px] sm:h-[280px] flex items-center justify-center bg-slate-50 rounded-lg">
                  <div className="text-center">
                    <RefreshCw className="h-6 w-6 sm:h-8 sm:w-8 text-brand-teal mx-auto mb-2 animate-spin" />
                    <p className="text-xs sm:text-sm text-slate-600">Loading revenue data...</p>
                  </div>
                </div>
              ) : revenueData.length > 0 ? (
                <div className="h-[220px] sm:h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueData} margin={{ top: 10, right: 5, left: -15, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorRevenuePayments" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#067A76" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#067A76" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis
                        dataKey="formattedDate"
                        stroke="#64748b"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        stroke="#64748b"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={value => `$${value}`}
                        width={40}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="amount"
                        stroke="#067A76"
                        strokeWidth={2}
                        fill="url(#colorRevenuePayments)"
                        dot={{ fill: '#067A76', strokeWidth: 2, r: 3 }}
                        activeDot={{ r: 5, fill: '#067A76' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[220px] sm:h-[280px] flex items-center justify-center bg-slate-50 rounded-lg">
                  <div className="text-center">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <TrendingUp className="h-6 w-6 sm:h-7 sm:w-7 text-slate-300" strokeWidth={1.5} />
                    </div>
                    <p className="text-sm text-slate-600 font-medium">No revenue data available</p>
                    <p className="text-xs text-slate-500 mt-1">for the selected period</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Transactions - Matching Dashboard Style */}
          <Card>
            <CardHeader className="pb-2 px-3 sm:px-6">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-brand-teal/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-brand-teal" strokeWidth={2} />
                </div>
                <div>
                  <CardTitle className="text-slate-900 font-montserrat font-semibold text-sm sm:text-base">
                    Recent Product Sales
                  </CardTitle>
                  <p className="text-slate-500 text-xs sm:text-sm mt-0.5">Your latest product transactions</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-2 sm:px-6">
              {transactions.length > 0 ? (
                <div className="overflow-x-auto -mx-2 sm:mx-0">
                  <table className="w-full min-w-[500px]">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left py-2 sm:py-3 px-2 text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">#</th>
                        <th className="text-left py-2 sm:py-3 px-2 text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">Patient</th>
                        <th className="text-left py-2 sm:py-3 px-2 text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Product</th>
                        <th className="text-right py-2 sm:py-3 px-2 text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                        <th className="text-right py-2 sm:py-3 px-2 text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.slice(0, 5).map((transaction, index) => (
                        <tr key={transaction.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                          <td className="py-2 sm:py-3 px-2">
                            <span className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-[10px] sm:text-xs font-medium bg-slate-100 text-slate-600">
                              {index + 1}
                            </span>
                          </td>
                          <td className="py-2 sm:py-3 px-2">
                            <span className="font-medium text-slate-900 text-xs sm:text-sm line-clamp-1">{transaction.patient_name}</span>
                          </td>
                          <td className="py-2 sm:py-3 px-2 hidden sm:table-cell">
                            <span className="text-slate-600 text-xs sm:text-sm line-clamp-1">
                              {transaction.productName || transaction.medication}
                            </span>
                          </td>
                          <td className="py-2 sm:py-3 px-2 text-right">
                            <span className="font-medium text-slate-900 text-xs sm:text-sm">
                              ${transaction.amount?.toFixed(2) || transaction.clinicRevenue?.toFixed(2)}
                            </span>
                            {transaction.splits?.find(s => s.type === 'clinic_revenue') && (
                              <p className="text-[10px] text-green-600">
                                +${transaction?.splits?.find(s => s.type === 'clinic_revenue')?.amount?.toFixed(2)} margin
                              </p>
                            )}
                          </td>
                          <td className="py-2 sm:py-3 px-2 text-right">
                            <Badge
                              className={`capitalize text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 hover:bg-transparent ${
                                transaction.status === 'succeeded' 
                                  ? 'bg-brand-teal/10 text-brand-teal hover:bg-brand-teal/10' 
                                  : transaction.status === 'pending'
                                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-100'
                                  : transaction.status === 'refunded'
                                  ? 'bg-brand-teal/10 text-brand-teal hover:bg-brand-teal/10'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              {transaction.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 sm:py-10">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <CreditCard className="h-6 w-6 sm:h-7 sm:w-7 text-slate-300" strokeWidth={1.5} />
                  </div>
                  <p className="text-slate-600 font-medium text-xs sm:text-sm">No recent transactions found</p>
                  <p className="text-slate-400 text-[10px] sm:text-xs mt-1">Transactions will appear as sales are made</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <Transactions dateRange={dateRange} />

        <Settlements dateRange={dateRange} />
      </Tabs>
    </div>
  );
};

export default ClinicPaymentReports;
